import { useLinkProps } from '@tanstack/react-router';
import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ForwardedRef,
} from 'react';

type SidebarLinkProps = {
  to: string;
  /**
   * Its page is on screen as the sidebar reads it: with a Clu chat in front
   * of the page, it is not.
   */
  current: boolean;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'aria-current'>;

/**
 * A router link that is the current page only when `current` says so. The
 * router's own Link marks itself current (aria-current, data-status and an
 * `active` class) whenever the path on screen falls under `to`, over the
 * caller's props, so it would stay current with a chat in front of its page
 * and could not be current on a path outside `to`.
 */
export const SidebarLink = forwardRef<HTMLAnchorElement, SidebarLinkProps>(
  function SidebarLink({ to, current, children, ...rest }, ref) {
    // The router types the ref as any Element; it lands on this anchor.
    const props = useLinkProps(
      { to, activeProps: {}, ...rest },
      ref as ForwardedRef<Element>
    );
    return (
      <a
        {...props}
        aria-current={current ? 'page' : undefined}
        data-status={current ? 'active' : undefined}
      >
        {children}
      </a>
    );
  }
);
